import React, { useRef, useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import mapboxgl from '!mapbox-gl'
import { Button, Tooltip } from '@mui/material'
import { Save } from '@mui/icons-material' // MapOutlined

import { mapBoxKeys, userRightTypes } from '../../../../constants'
import { ErrorsContext } from '../../../../contexts'
import { Logger, LoggerTypes } from '../../../../utils'
import { HeaderErrors, Icon, iconNames, SlideToggleContent, AccessControl } from '../../../../components'
import './style.scss'

mapboxgl.accessToken = 'pk.eyJ1Ijoic2JtLXRlc3QiLCJhIjoiY2t5NGZtcWw5MGJrOTJvcW5jZm0wNzgwbyJ9.H-bOofFfUdoKcqqJRcBmjQ'

const defaultValue = {
  latitude: 43.7384,
  longitude: 7.4246
}

/**
 * Generic Map used in a FlowGroup to display its location.
 *
 * @param {Modeler} modeler BPMN modeler object
 * @param {flowGroup} flowGroup Current flowGroup object
 * @param {func} onSave Callback function to save the current flowGroup with the modified BPMN XML.
 * @param {func} onSimulationList Callback function to open the simulation list screen.
 * @param {func} onSimulationLaunch Callback function to open the simulation launch screen.
 * @returns {SlideToggleContent}
 */
export const Map = ({ modeler, flowGroup, onSave, onSimulationList, onSimulationLaunch }) => {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const { t } = useTranslation()

  // -- BPMN XML tabs errors detected
  const errors = useContext(ErrorsContext)
  const numErrors = (errors && errors.length) || 0
  const hasError = numErrors > 0

  const [lat, setLat] = useState(defaultValue.latitude)
  const [lng, setLng] = useState(defaultValue.longitude)
  const [isOpen] = useState(false) // setIsOpen

  const flowGroupBpmnState = useSelector((state) => state.flowGroupBpmnState) || { isDirty: modeler.isDirty() }

  const id = (flowGroup && flowGroup.id) || 0

  useEffect(() => {
    if (map.current) {
      return
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapBoxKeys.MAP_BLUE,
      center: [lng, lat],
      zoom: 9
    })

    map.current.on('move', () => {
      setLng(map.current.getCenter().lng.toFixed(4))
      setLat(map.current.getCenter().lat.toFixed(4))
    })
  })

  // const onClickMap = (e) => {
  //   e.stopPropagation()
  //   e.preventDefault()

  //   Logger.trace(LoggerTypes.CONTROLLER, 'Map.onMapButtonClick()', { e, isOpen })

  //   setIsOpen(!isOpen)
  // }

  const onClickSave = async (e) => {
    e.stopPropagation()
    e.preventDefault()

    const { xml } = (await modeler.getXML()) || null
    const viewbox = modeler.getViewbox() || null

    const _flowGroup = {
      ...flowGroup,
      xml,
      canvas_height: viewbox.height,
      canvas_width: viewbox.width,
      canvas_scale: viewbox.scale,
      canvas_x: viewbox.x,
      canvas_y: viewbox.y
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickSave()', { modeler, xml, _flowGroup })

    if (modeler && onSave && xml) {
      onSave(_flowGroup)
    }
  }

  const onClickSimulationList = (e) => {
    e.stopPropagation()
    e.preventDefault()

    Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickSimulationList()', { modeler, flowGroupBpmnState })

    onSimulationList()
  }

  const onClickSimulationLaunch = (e) => {
    e.stopPropagation()
    e.preventDefault()

    Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickSimulationLaunch()', { modeler, flowGroupBpmnState })

    onSimulationLaunch()
  }

  // const onClickUpload = async (e) => {
  //   e.stopPropagation()
  //   e.preventDefault()

  //   Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickUpload()')

  //   // (await modeler.getXML())
  // }

  // const onClickDownload = async (e) => {
  //   e.stopPropagation()
  //   e.preventDefault()

  //   Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickDownload()')

  //   await modeler.saveXML()
  // }

  // const onClickSvg = async (e) => {
  //   e.stopPropagation()
  //   e.preventDefault()

  //   Logger.trace(LoggerTypes.CONTROLLER, 'Bpmn.onClickSvg()')(await modeler.saveSVG())
  // }

  // Logger.trace(LoggerTypes.CONTAINER, 'Map.render()', { isOpen, flowGroupBpmnState, errors })

  return (
    <SlideToggleContent isOpen={isOpen} className={`map-${id} map-main`}>
      <footer className="buttons-actions">
        <div className="footer_left">
          {/* --  MAP -- */}
          {/* <Tooltip title={t('flowGroups.map.title')}>
            <Button className="footer-button" onClick={onClickMap}>
              <MapOutlined />
            </Button>
          </Tooltip> */}

          {/* --  SAVE -- */}
          {/* {!flowGroupBpmnState.isDirty ? (
            <Button className="footer-button" disabled={true} onClick={onClickSave}>
              <Save />
            </Button>
          ) : ( */}
          <AccessControl userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
            <Tooltip title={t('common.save')}>
              <Button className="footer-button" onClick={onClickSave}>
                <Save />
              </Button>
            </Tooltip>
          </AccessControl>

          {/* )} */}

          {/* -- ERRORS -- */}
          {hasError ? (
            <Tooltip title={t('common.errors')}>
              <div className="footer-button">
                <HeaderErrors />
              </div>
            </Tooltip>
          ) : (
            <div className="footer-button" style={{ opacity: '0.7' }}>
              <HeaderErrors />
            </div>
          )}

          {/* -- SVG -- */}
          {/* <Tooltip title={t('common.svg')}>
            <Button className="footer-button" onClick={onClickSvg}>
              <Photo />
            </Button>
          </Tooltip> */}

          {/* -- XML -- */}
          {/* <Tooltip title={t('common.xml')}>
            <Button className="footer-button" onClick={onClickDownload}>
              <Code />
            </Button>
          </Tooltip> */}

          {/* -- UPLOAD -- */}
          {/* <Tooltip title={t('common.upload')}>
            <Button className="footer-button" onClick={onClickUpload}>
              <CloudUpload />
            </Button>
          </Tooltip> */}
        </div>

        <div className="footer_right">
          <Tooltip title={t('simulations.list.title')}>
            <Button className="footer-button" onClick={onClickSimulationList}>
              <Icon name={iconNames.SIMULATION} size={23} />
            </Button>
          </Tooltip>
          <AccessControl userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
            <Tooltip title={t('simulations.launch.title')}>
              <Button className="footer-button" onClick={onClickSimulationLaunch}>
                <Icon name={iconNames.LAUNCH} size={23} />
              </Button>
            </Tooltip>
          </AccessControl>
        </div>
      </footer>

      <div className="sidebar-lat-lng">
        {t('flowGroups.map.longitude')}: {lng} | {t('flowGroups.map.latitude')}: {lat}
      </div>

      <div ref={mapContainer} className="map-container"></div>
    </SlideToggleContent>
  )
}

Map.displayName = 'Map'

Map.propTypes = {
  flowGroup: PropTypes.object.isRequired,
  modeler: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onSimulationList: PropTypes.func.isRequired,
  onSimulationLaunch: PropTypes.func.isRequired
}

Map.defaultProps = {
  flowGroup: {},
  modeler: {},
  onSave: () => {},
  onSimulationList: () => {},
  onSimulationLaunch: () => {}
}
